import React, { useEffect, useState, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { useAppContext } from '../../../providers/AppProvider';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  DataZoomComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import BucketRangeButtons from './BucketRangeButtons';
import { FallingLines } from 'react-loader-spinner';
import axios from 'axios';
import dayjs from 'dayjs';
import getShiftStartEndTime from '../utils/chartUtils';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
  // DataZoomComponent
]);

const getDefaultOptions = (theme, getThemeColor, data) => {
  const sortedData = data.sort((a, b) => new Date(a[0]) - new Date(b[0]));

  // Extract all values for dynamic min and max calculations
  const values = sortedData.map(item => item[1]);
  const dynamicMin =
    values.length > 0 ? (Math.min(...values) / 1.1).toFixed(2) : 0;
  const dynamicMax =
    values.length > 0 ? (Math.max(...values) * 1.1).toFixed(2) : 100;

  return {
    color: [getThemeColor('primary'), getThemeColor('info')],
    tooltip: {
      trigger: 'axis',
      padding: 10,
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'line',
        axis: 'x'
      },
      formatter: function (params) {
        const value = params[0].value[1];
        const time = params[0].value[0];
        return `Time: ${dayjs(time).format(
          'HH:mm:ss'
        )}<br/>Value: ${value.toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,

      axisLabel: {
        formatter: value => dayjs(value).format('HH:mm'),
        interval: 'auto',
        margin: 10
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      position: 'right',
      axisPointer: { type: 'none' },
      axisTick: { show: false },
      splitLine: {
        show: false
      },
      axisLine: { show: false },
      axisLabel: { show: true },
      min: dynamicMin,
      max: dynamicMax
    },
    series: [
      {
        type: 'line',
        data: data,
        showSymbol: false,
        symbol: 'circle',
        smooth: true,
        areaStyle: {}
      }
    ],
    grid: {
      right: 20,
      left: 20,
      bottom: '20px',
      top: '2%',
      containLabel: true
    }
    // animation: true,
    // dataZoom: [
    //   {
    //     type: 'inside',
    //     xAxisIndex: 0,
    //     start: 0,
    //     end: 100
    //   }
    // ]
  };
};

const MachineTotalMetersChart = ({
  bucket,
  dynamicBucket = false,
  machineName,
  fields,
  startTime,
  endTime
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true); // Track loading state
  const [currentBucket, setCurrentBucket] = useState(bucket);

  const fetchData = async () => {
    setLoading(true); // Set loading to true when fetching starts
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${currentBucket}`
      );

      setData(response.data.total_produced);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false); // Set loading to false when fetching is done
    }
  };

  const updateBucket = bucketName => {
    setCurrentBucket(bucketName);
  };

  const getIntervalTime = () => {
    return 60 * 1000;
  };

  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const option = getDefaultOptions(theme, getThemeColor, data);

  useEffect(() => {
    fetchData();
    const intervalTime = getIntervalTime();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, intervalTime) : null;
    return () => clearInterval(intervalId);
  }, [currentBucket, startTime, endTime]);

  if (loading) {
    return (
      <Card className="h-0">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{'Total Length Produced on time'}</div>
          {dynamicBucket && (
            <BucketRangeButtons
              current={currentBucket}
              onChange={updateBucket}
            />
          )}
        </div>
        <ReactEChartsCore
          echarts={echarts}
          option={option}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  headerContainer: {
    display: 'flex', // Flexbox for layout
    justifyContent: 'space-between', // Title on the left, buttons on the right
    alignItems: 'center',
    marginBottom: '10px' // Space between title/buttons and chart
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    // minWidth lets a long title wrap instead of shoving the resolution
    // picker out of the card: a flex item will not shrink below its
    // content width until it is told it may.
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.25,
    paddingRight: '8px'
  },
};

export default memo(MachineTotalMetersChart);
